import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SaveIcon from '@mui/icons-material/Save';
import ArcaPdfStudioLayout from '../../../components/arca/ArcaPdfStudioLayout';
import { appendUploadFiles } from '../../../components/arca/uploadItemUtils';
import { paDownloadUrl, paJson, paUpload } from './paApi';

function defaultEmail(data) {
  const num = data?.numeroProcesso || data?.id || '';
  const emp = data?.contratoEmpresa || '';
  const tipo = data?.tipo === 'aditivo' ? 'Aditivo' : 'Contratação';
  return {
    subject: `Processo Administrativo ${num}${emp ? ` — ${emp}` : ''} — ${tipo}`,
    body: `Prezados(as),

Segue em anexo a documentação do Processo Administrativo nº ${num}${
      data?.contratoVinculado ? `, referente ao Contrato nº ${data.contratoVinculado}` : ''
    }${emp ? ` (${emp})` : ''}.

Objeto: ${data?.objetoContrato || '—'}.

Solicito análise e providências cabíveis.

Atenciosamente,`,
  };
}

export function PaDocumentosPanel({
  processoId,
  unidadeSelecionada,
  documentos = [],
  onChanged,
  onError,
  onOk,
  busy,
  setBusy,
}) {
  const [pending, setPending] = useState([]);
  const [localError, setLocalError] = useState('');
  const [localOk, setLocalOk] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    setSelected(new Set((documentos || []).map((d) => d.id)));
  }, [documentos]);

  const addFiles = useCallback((files) => {
    setLocalError('');
    const n = appendUploadFiles(files, setPending, { multiple: true });
    if (!n) setLocalError('Selecione arquivos PDF válidos.');
  }, []);

  const uploadPending = async () => {
    if (!pending.length) return;
    setBusy(true);
    setLocalError('');
    try {
      const fd = new FormData();
      pending.forEach((it) => fd.append('files', it.file, it.file.name));
      await paUpload(
        `/api/arca/processo-administrativo/${processoId}/documentos`,
        unidadeSelecionada,
        fd
      );
      setPending([]);
      const msg = 'Documentos anexados ao processo.';
      setLocalOk(msg);
      onOk?.(msg);
      await onChanged?.();
    } catch (e) {
      setLocalError(e.message);
      onError?.(e.message);
    } finally {
      setBusy(false);
    }
  };

  const removeDoc = async (docId) => {
    if (!window.confirm('Remover este anexo do processo?')) return;
    setBusy(true);
    try {
      await paJson(
        `/api/arca/processo-administrativo/${processoId}/documentos/${docId}`,
        unidadeSelecionada,
        { method: 'DELETE' }
      );
      const msg = 'Documento removido.';
      setLocalOk(msg);
      onOk?.(msg);
      await onChanged?.();
    } catch (e) {
      setLocalError(e.message);
      onError?.(e.message);
    } finally {
      setBusy(false);
    }
  };

  const juntar = async () => {
    const ids = [...selected];
    if (ids.length < 2) {
      const msg = 'Selecione ao menos 2 PDFs para juntar.';
      setLocalError(msg);
      onError?.(msg);
      return;
    }
    setBusy(true);
    setLocalError('');
    try {
      await paJson(
        `/api/arca/processo-administrativo/${processoId}/documentos/juntar`,
        unidadeSelecionada,
        { method: 'POST', body: JSON.stringify({ ids }) }
      );
      const msg = 'PDF juntado e salvo nos documentos do processo.';
      setLocalOk(msg);
      onOk?.(msg);
      await onChanged?.();
    } catch (e) {
      setLocalError(e.message);
      onError?.(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Box>
      <ArcaPdfStudioLayout
        title="Documentos"
        info="Arraste e solte para reordenar. Clique no thumbnail para visualizar. Use ↻ para girar cada PDF. Depois salve no processo."
        limits={
          <>
            PDFs · até <strong>40 MB</strong> cada. Após salvar, os arquivos ficam vinculados a este
            processo administrativo.
          </>
        }
        showBack={false}
        maxWidth="100%"
        error={localError}
        onClearError={() => setLocalError('')}
        onRotateError={setLocalError}
        success={localOk}
        onClearSuccess={() => setLocalOk('')}
        items={pending}
        setItems={setPending}
        loading={busy}
        dropzone={{
          accept: 'application/pdf,.pdf',
          multiple: true,
          maxSizeMb: 40,
          onFilesAdd: addFiles,
          label: 'Arraste os PDFs do processo ou clique',
          hint: 'CI, checklist, propostas, pareceres… · clique no card para visualizar · ↻ para girar',
        }}
        actionLabel={
          pending.length
            ? `Salvar anexos no processo (${pending.length})`
            : 'Salvar anexos no processo'
        }
        actionLoadingLabel="Salvando…"
        actionStartIcon={<CloudUploadIcon />}
        onAction={uploadPending}
        actionDisabled={!pending.length}
        clearLabel="Limpar seleção"
        sidebarExtras={
          documentos.length > 0 ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              {documentos.length} documento(s) já salvos neste processo (lista abaixo).
            </Alert>
          ) : null
        }
        belowStudio={
          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
                mb: 1.5,
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                Documentos salvos no processo ({documentos.length})
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CallMergeIcon />}
                disabled={busy || selected.size < 2}
                onClick={juntar}
              >
                Juntar PDFs selecionados
              </Button>
            </Box>
            {!documentos.length ? (
              <Alert severity="info">
                Nenhum documento salvo ainda. Adicione PDFs na grade acima e clique em Salvar.
              </Alert>
            ) : (
              <Paper
                elevation={0}
                sx={{ border: '1px solid rgba(25,118,210,0.12)', borderRadius: 2 }}
              >
                <List dense>
                  {documentos.map((d) => (
                    <ListItem key={d.id} divider>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={selected.has(d.id)}
                          onChange={() => toggle(d.id)}
                          disabled={busy}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={d.nomeOriginal || d.path}
                        secondary={d.mime || ''}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          component="a"
                          href={paDownloadUrl(
                            `/api/arca/processo-administrativo/${processoId}/documentos/${d.id}/download`,
                            unidadeSelecionada
                          )}
                          target="_blank"
                          rel="noreferrer"
                          aria-label="baixar"
                        >
                          <DownloadIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="remover"
                          disabled={busy}
                          onClick={() => removeDoc(d.id)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>
        }
      />
    </Box>
  );
}

export function PaEncaminharPanel({
  processoId,
  unidadeSelecionada,
  data,
  documentos = [],
  onChanged,
  onError,
  onOk,
  busy,
  setBusy,
}) {
  const defaults = useMemo(() => defaultEmail(data), [data]);
  const [subject, setSubject] = useState(data?.email?.subject || defaults.subject);
  const [body, setBody] = useState(data?.email?.body || defaults.body);
  const [emailsExtra, setEmailsExtra] = useState('');
  const [juntar, setJuntar] = useState(true);
  const [selected, setSelected] = useState(() => new Set((documentos || []).map((d) => d.id)));
  const [aiFlag, setAiFlag] = useState(false);

  useEffect(() => {
    if (data?.email?.subject) setSubject(data.email.subject);
    else setSubject(defaults.subject);
    if (data?.email?.body) setBody(data.email.body);
    else setBody(defaults.body);
  }, [data?.email?.subject, data?.email?.body, defaults.subject, defaults.body]);

  useEffect(() => {
    setSelected(new Set((documentos || []).map((d) => d.id)));
  }, [documentos]);

  const destResumo = useMemo(() => {
    const list = [];
    (data?.destinatarios || []).forEach((d) => {
      if (d.userEmail) list.push(`${d.userNome || ''} <${d.userEmail}>`.trim());
      if (d.externalEmail) list.push(d.externalEmail);
    });
    return list;
  }, [data?.destinatarios]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const salvarRascunho = async () => {
    setBusy(true);
    try {
      await paJson(`/api/arca/processo-administrativo/${processoId}/email`, unidadeSelecionada, {
        method: 'PUT',
        body: JSON.stringify({ subject, body, geradoPorIa: aiFlag }),
      });
      onOk?.('Rascunho de e-mail salvo.');
      await onChanged?.();
    } catch (e) {
      onError?.(e.message);
    } finally {
      setBusy(false);
    }
  };

  const melhorarIa = async () => {
    setBusy(true);
    try {
      const r = await paJson(
        `/api/arca/processo-administrativo/${processoId}/email/melhorar-ia`,
        unidadeSelecionada,
        { method: 'POST', body: JSON.stringify({ subject, body }) }
      );
      if (r.subject) setSubject(r.subject);
      if (r.body) setBody(r.body);
      setAiFlag(true);
      onOk?.('E-mail melhorado pela IA — revise e envie.');
    } catch (e) {
      onError?.(e.message);
    } finally {
      setBusy(false);
    }
  };

  const enviar = async () => {
    if (!destResumo.length && !emailsExtra.trim()) {
      onError?.('Cadastre destinatários na aba Destinatários ou informe e-mails extras.');
      return;
    }
    if (!selected.size) {
      onError?.('Selecione ao menos um documento para anexar.');
      return;
    }
    if (
      !window.confirm(
        `Enviar o processo por e-mail para ${destResumo.length || 'os'} destinatário(s) salvos${
          emailsExtra.trim() ? ' + e-mails extras' : ''
        }?`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const r = await paJson(
        `/api/arca/processo-administrativo/${processoId}/email/enviar`,
        unidadeSelecionada,
        {
          method: 'POST',
          body: JSON.stringify({
            subject,
            body,
            geradoPorIa: aiFlag,
            documentoIds: [...selected],
            juntar,
            emailsExtra,
          }),
        }
      );
      onOk?.(r.message || `Enviado para: ${(r.para || []).join(', ')}`);
      await onChanged?.();
    } catch (e) {
      onError?.(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Use os destinatários da aba <strong>Destinatários</strong> (usuários do sistema + e-mails
        externos). Anexe os arquivos da aba Documentos.
      </Typography>

      {data?.email?.enviadoEm && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Último envio registrado em {data.email.enviadoEm}.
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid rgba(25,118,210,0.12)', borderRadius: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Destinatários cadastrados
        </Typography>
        {destResumo.length ? (
          destResumo.map((e) => (
            <Typography key={e} variant="body2">
              • {e}
            </Typography>
          ))
        ) : (
          <Alert severity="warning">Nenhum destinatário. Vá na aba Destinatários e salve.</Alert>
        )}
        <TextField
          fullWidth
          margin="dense"
          label="E-mails extras nesta remessa (opcional)"
          placeholder="fiscal@..., financeiro@..."
          value={emailsExtra}
          onChange={(e) => setEmailsExtra(e.target.value)}
          helperText="Somam-se aos destinatários já salvos"
        />
      </Paper>

      <TextField
        fullWidth
        margin="dense"
        label="Assunto"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
      />
      <TextField
        fullWidth
        margin="dense"
        label="Corpo do e-mail"
        multiline
        minRows={10}
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Anexos desta remessa
      </Typography>
      {!documentos.length ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Nenhum documento. Vá em Documentos e anexe os arquivos.
        </Alert>
      ) : (
        <Paper elevation={0} sx={{ mb: 1, border: '1px solid rgba(25,118,210,0.12)', borderRadius: 2 }}>
          <List dense>
            {documentos.map((d) => (
              <ListItem key={d.id} dense>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge="start"
                    checked={selected.has(d.id)}
                    onChange={() => toggle(d.id)}
                  />
                </ListItemIcon>
                <ListItemText primary={d.nomeOriginal || d.path} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
      <FormControlLabel
        control={
          <Checkbox checked={juntar} onChange={(e) => setJuntar(e.target.checked)} />
        }
        label="Juntar PDFs selecionados em um único anexo (recomendado)"
      />

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<AutoAwesomeIcon />}
          disabled={busy}
          onClick={melhorarIa}
        >
          Melhorar com IA
        </Button>
        <Button variant="outlined" startIcon={<SaveIcon />} disabled={busy} onClick={salvarRascunho}>
          Salvar rascunho
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
          disabled={busy}
          onClick={enviar}
        >
          Enviar processo por e-mail
        </Button>
      </Box>
    </Box>
  );
}
